import {
  AdjustResult,
  AloftPrice,
  PairedSalesAnalysisFeature,
  StudiesPair,
} from '@aloft/toolkit-shared';
import { Typography } from '@aloft/toolkit-sui';

import { TPColumnConfig } from '../../components/ToolkitPropertyTable/types.ts';
import { isPairedSalesSqFtFeature } from '../studies/types.ts';
import { getTotalAdjustedValuePaired } from './getTotalAdjustedValuePaired.ts';

function getUnit(feature: PairedSalesAnalysisFeature): string {
  if (isPairedSalesSqFtFeature(feature)) return '/sf';
  return '';
}

export type StudiesData = StudiesPair & {
  result: AdjustResult;
  feature: PairedSalesAnalysisFeature;
};

export const SHARED_RECONCILIATION_COLUMNS = [
  'address',
  'salePrice',
  {
    id: 'custom$adjustedPrice',
    title: 'Adj Price',
    selector: (x: StudiesData) => [
      x.p1.salePrice ?? AloftPrice.zero,
      getTotalAdjustedValuePaired(x.p2.salePrice ?? AloftPrice.zero, x.adjustments),
    ],
    toCell: ([p1, p2]: [AloftPrice, AloftPrice]) => (
      <span style={{ display: 'flex', flexDirection: 'column' }}>
        <span>
          <Typography variant="tableH2" truncate>
            {p1.toString(true)}
          </Typography>
        </span>
        <span>
          <Typography variant="tableH2" truncate>
            {p2.toString(true)}
          </Typography>
        </span>
      </span>
    ),
    sortBy: (a: AloftPrice, b: AloftPrice) =>
      a === b ? 0 : (a.unitAmount ?? -Infinity) - (b.unitAmount ?? -Infinity),
  },
  {
    id: 'custom$difference',
    title: 'Difference',
    selector: (x: StudiesData) => x,
    toCell: ({ result: { t, value }, feature }: StudiesData) => (
      <Typography variant="tableH2" truncate>
        {t === 'absolute'
          ? `${(value as AloftPrice).toString(true)}${getUnit(feature)}`
          : `${value}%`}
      </Typography>
    ),
    sortBy: (a: StudiesData, b: StudiesData) =>
      a === b
        ? 0
        : (a.result.t === 'absolute' ? (a.result.value.unitAmount ?? -Infinity) : a.result.value) -
          (b.result.t === 'absolute' ? (b.result.value.unitAmount ?? -Infinity) : b.result.value),
  },
] as const;

export const SHARED_RECONCILIATION_COLUMN_CONFIG: TPColumnConfig<StudiesData, unknown[]> = {
  toValue: (get, data) => {
    return [get(data.p1), get(data.p2)];
  },
  toCell: ({ value: [p1, p2], toString }) => (
    <span style={{ display: 'flex', flexDirection: 'column' }}>
      <span>
        <Typography variant="tableH2" truncate>
          {toString(p1)}
        </Typography>
      </span>
      <span>
        <Typography variant="tableH2" truncate>
          {toString(p2)}
        </Typography>
      </span>
    </span>
  ),
  toCompareValues: (rowAValues, rowBValues) => {
    const a = rowAValues?.[0];
    const b = rowBValues?.[0];
    return [a, b];
  },
};
